import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Trophy, LogOut, LayoutDashboard, TrendingUp, Download } from 'lucide-react';

export default function AdminResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/admin/results`);
      setResults(response.data);
    } catch (error) {
      toast.error('Failed to fetch results');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleExportResults = () => {
    // Simple CSV export
    const csvContent = [
      ['Student Name', 'Exam', 'Score', 'Total Marks', 'Percentage', 'Time Spent (min)', 'Date'].join(','),
      ...results.map(r => [
        r.student_name,
        r.exam_title,
        r.score,
        r.total_marks,
        ((r.score / r.total_marks) * 100).toFixed(1) + '%',
        Math.floor(r.time_spent_seconds / 60),
        new Date(r.submitted_at).toLocaleString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exam-results-${Date.now()}.csv`;
    a.click();
    toast.success('Results exported successfully');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-sky-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="h-7 w-7 text-sky-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>ESAP Admin</h1>
              <p className="text-sm text-gray-500">Results & Analytics</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn" className="border-sky-200">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-6 py-3">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="nav-dashboard">
              Dashboard
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/students')} data-testid="nav-students">
              Students
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/exams')} data-testid="nav-exams">
              Exams
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/categories')} data-testid="nav-categories">
              Categories
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/question-bank')} data-testid="nav-question-bank">
              Question Bank
            </Button>
            <Button variant="ghost" className="text-sky-600" onClick={() => navigate('/admin/results')} data-testid="nav-results">
              Results
            </Button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Exam Results</h2>
            <p className="text-gray-600">View and analyze student performance</p>
          </div>
          {results.length > 0 && (
            <Button onClick={handleExportResults} data-testid="export-results-btn" className="bg-sky-600 hover:bg-sky-700">
              <Download className="h-4 w-4 mr-2" />
              Export as CSV
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-500">Loading results...</p>
          </div>
        ) : results.length === 0 ? (
          <Card className="border-sky-100">
            <CardContent className="py-12 text-center">
              <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No results yet. Students need to complete exams first.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {results.map((result) => {
              const percentage = ((result.score / result.total_marks) * 100).toFixed(1);
              const passed = percentage >= 50;
              
              return (
                <Card key={result.id} className="border-sky-100 hover:shadow-md transition-shadow" data-testid={`result-card-${result.id}`}>
                  <CardContent className="py-5">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">{result.student_name}</h3>
                          <Badge className={passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {passed ? 'Passed' : 'Failed'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{result.exam_title}</p>
                        <div className="flex gap-6 text-sm text-gray-600">
                          <span>
                            Score: <span className="font-semibold text-sky-600">{result.score}/{result.total_marks}</span>
                          </span>
                          <span>
                            Percentage: <span className="font-semibold text-sky-600">{percentage}%</span>
                          </span>
                          <span>Time: {Math.floor(result.time_spent_seconds / 60)} min</span>
                          <span>Date: {new Date(result.submitted_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <div className="text-3xl font-bold text-sky-600">{percentage}%</div>
                          <div className="text-xs text-gray-500">Score</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}